import { PackageReadinessPreview } from './HrSettingsTools';
// components/hr/OnboardingPackageModal.tsx
import React, { useRef, useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Switch,
  Button,
  Divider,
  List,
  IconButton,
  Chip,
  Card,
  Grid,
  Alert,
} from '@mui/joy';
import {
  Plus as AddOutlined,
  Trash as DeleteOutlined,
  CheckCircle as CheckCircleOutlined,
  ArrowUp as ArrowUpward,
  ArrowDown as ArrowDownward,
} from '@phosphor-icons/react';
import {
  useCreateOnboardingPackage,
  useUpdateOnboardingPackage,
  useOnboardingPackage,
  useHrSettings,
} from '../../hooks/useHr';
import type {
  OnboardingStep,
  OnboardingStepType,
  OnboardingPackageAppliesTo,
  CreateOnboardingPackageRequest,
} from '../../types/hr';

interface OnboardingPackageModalProps {
  open: boolean;
  onClose: () => void;
  packageId?: string; // If provided, we're editing
  onSuccess?: () => void;
}

const STEP_TYPES: { type: OnboardingStepType; label: string; description: string }[] = [
  { type: 'welcome', label: 'Welcome', description: 'Welcome message and overview' },
  { type: 'identity', label: 'Identity Verification', description: 'Verify identity documents' },
  { type: 'personal_details', label: 'Personal Details', description: 'Collect personal information' },
  { type: 'employment_type', label: 'Employment Type', description: 'Confirm employment classification' },
  { type: 'tax_forms', label: 'Tax Forms', description: 'Complete W-4/W-9 tax forms' },
  { type: 'document_signing', label: 'Document Signing', description: 'Sign required documents' },
  { type: 'direct_deposit', label: 'Direct Deposit', description: 'Set up direct deposit' },
  { type: 'training', label: 'Training', description: 'Complete required training' },
  { type: 'review', label: 'Review', description: 'Review all submitted information' },
  { type: 'completion', label: 'Completion', description: 'Onboarding complete confirmation' },
];

const DEFAULT_STEPS: OnboardingStep[] = [
  { id: 'welcome', title: 'Welcome', type: 'welcome', required: true, order: 0 },
  { id: 'personal', title: 'Personal Information', type: 'personal_details', required: true, order: 1 },
  { id: 'tax', title: 'Tax Information', type: 'tax_forms', required: true, order: 2 },
  { id: 'documents', title: 'Document Signing', type: 'document_signing', required: true, order: 3 },
  { id: 'review', title: 'Review & Submit', type: 'review', required: true, order: 4 },
];

// Where a new package starts until the org's own default arrives. Named so the
// late-arrival effect can tell its own value from one the user typed.
const FALLBACK_EXPIRATION_DAYS = 7;

export const OnboardingPackageModal: React.FC<OnboardingPackageModalProps> = ({
  open,
  onClose,
  packageId,
  onSuccess,
}) => {
  const isEditing = !!packageId;

  const { data: existingPackage, isLoading: loadingPackage } = useOnboardingPackage(packageId || '');
  const { data: hrSettings } = useHrSettings();
  const createPackage = useCreateOnboardingPackage();
  const updatePackage = useUpdateOnboardingPackage();

  // Team → Settings shows this as "Default Onboarding Link Expiration", but the
  // package's own value is what actually governs expiry - so a new package has
  // to start from the setting or the setting means nothing.
  const createDefaults = (): CreateOnboardingPackageRequest => ({
    name: '',
    description: '',
    applies_to: 'all',
    steps: DEFAULT_STEPS,
    is_active: true,
    is_default: false,
    welcome_message: '',
    completion_message: '',
    link_expiration_days: hrSettings?.default_link_expiration_days ?? FALLBACK_EXPIRATION_DAYS,
  });

  const [formData, setFormData] = useState<CreateOnboardingPackageRequest>(createDefaults);
  // Whether the org's default expiry has been folded into this form yet.
  const settingsSeededRef = useRef(false);

  const [error, setError] = useState<string>('');

  // Load existing package data when editing
  useEffect(() => {
    if (isEditing && existingPackage) {
      setFormData({
        name: existingPackage.name,
        description: existingPackage.description,
        applies_to: existingPackage.applies_to,
        steps: existingPackage.steps,
        is_active: existingPackage.is_active,
        is_default: existingPackage.is_default,
        is_rehire_package: existingPackage.is_rehire_package,
        welcome_message: existingPackage.welcome_message,
        completion_message: existingPackage.completion_message,
        link_expiration_days: existingPackage.link_expiration_days,
      });
    }
  }, [isEditing, existingPackage]);

  // Reset form when modal opens for create mode
  useEffect(() => {
    if (open && !isEditing) {
      setFormData(createDefaults());
      settingsSeededRef.current = !!hrSettings;
      setError('');
    }
  }, [open, isEditing]);

  // The modal can be opened before the settings query resolves. Apply the org's
  // default when it lands, once, and only while the field still holds the
  // fallback - an expiration typed in the meantime is the user's choice.
  useEffect(() => {
    if (!open || isEditing || !hrSettings || settingsSeededRef.current) return;
    settingsSeededRef.current = true;
    setFormData((prev) => ({
      ...prev,
      link_expiration_days:
        prev.link_expiration_days === FALLBACK_EXPIRATION_DAYS
          ? hrSettings.default_link_expiration_days
          : prev.link_expiration_days,
    }));
  }, [open, isEditing, hrSettings]);

  const handleAddStep = () => {
    const newStep: OnboardingStep = {
      id: `step_${Date.now()}`,
      title: 'New Step',
      type: 'personal_details',
      required: true,
      order: formData.steps?.length || 0,
    };
    setFormData((prev) => ({
      ...prev,
      steps: [...(prev.steps || []), newStep],
    }));
  };

  const handleRemoveStep = (stepId: string) => {
    setFormData((prev) => ({
      ...prev,
      steps: (prev.steps || [])
        .filter((s) => s.id !== stepId)
        .map((s, i) => ({ ...s, order: i })),
    }));
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    const steps = [...(formData.steps || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;

    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    steps.forEach((s, i) => (s.order = i));

    setFormData((prev) => ({ ...prev, steps }));
  };

  const handleUpdateStep = (stepId: string, updates: Partial<OnboardingStep>) => {
    setFormData((prev) => ({
      ...prev,
      steps: (prev.steps || []).map((s) =>
        s.id === stepId ? { ...s, ...updates } : s
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Package name is required');
      return;
    }

    if (!formData.steps?.length) {
      setError('At least one step is required');
      return;
    }

    try {
      if (isEditing && packageId) {
        await updatePackage.mutateAsync({ id: packageId, data: formData });
      } else {
        await createPackage.mutateAsync(formData);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError('Failed to save package. Please try again.');
    }
  };

  const isLoading = createPackage.isPending || updatePackage.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 800,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <ModalClose />
        <DialogTitle>
          {isEditing ? 'Edit Onboarding Package' : 'Create Onboarding Package'}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid xs={12} md={6}>
              <FormControl sx={{ mb: 2 }}><FormLabel>Returning team members only</FormLabel><Switch checked={formData.is_rehire_package ?? false} onChange={e => setFormData({ ...formData, is_rehire_package: e.target.checked })} /><Typography level="body-xs">Use a rehire package for returning W-2 or contractor team members.</Typography></FormControl>
          <FormControl required sx={{ mb: 2 }}>
                <FormLabel>Package Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Standard Employee Onboarding"
                />
              </FormControl>
            </Grid>

            <Grid xs={12} md={6}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Applies To</FormLabel>
                <Select
                  value={formData.applies_to}
                  onChange={(_, value) => setFormData((prev) => ({ ...prev, applies_to: value as OnboardingPackageAppliesTo }))}
                >
                  <Option value="all">All Team Members</Option>
                  <Option value="w2">W-2 Employees Only</Option>
                  <Option value="1099">1099 Contractors Only</Option>
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this onboarding package"
                  minRows={2}
                />
              </FormControl>
            </Grid>

            <Grid xs={12} md={4}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Link Expiration (days)</FormLabel>
                <Input
                  type="number"
                  value={formData.link_expiration_days}
                  onChange={(e) => setFormData((prev) => ({ ...prev, link_expiration_days: parseInt(e.target.value) || 7 }))}
                  slotProps={{ input: { min: 1, max: 30 } }}
                />
              </FormControl>
            </Grid>

            <Grid xs={12} md={4}>
              <FormControl sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', mt: 3 }}>
                  <FormLabel>Active</FormLabel>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                </Box>
              </FormControl>
            </Grid>

            <Grid xs={12} md={4}>
              <FormControl sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', mt: 3 }}>
                  <FormLabel>Default Package</FormLabel>
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_default: e.target.checked }))}
                  />
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Steps Builder */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="title-md">Onboarding Steps</Typography>
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              startDecorator={<AddOutlined />}
              onClick={handleAddStep}
            >
              Add Step
            </Button>
          </Box>

          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            Drag to reorder steps. Each step will be shown to new hires in order.
          </Typography>

          <List>
            {(formData.steps || []).map((step, index) => (
              <Card key={step.id} variant="outlined" sx={{ mb: 1 }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        disabled={index === 0}
                        onClick={() => handleMoveStep(index, 'up')}
                      >
                        <ArrowUpward size={18} />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        disabled={index === (formData.steps?.length || 0) - 1}
                        onClick={() => handleMoveStep(index, 'down')}
                      >
                        <ArrowDownward size={18} />
                      </IconButton>
                    </Box>

                    <Chip size="sm" variant="soft" color="neutral">
                      Step {index + 1}
                    </Chip>

                    <Input
                      size="sm"
                      value={step.title}
                      onChange={(e) => handleUpdateStep(step.id, { title: e.target.value })}
                      sx={{ flex: 1 }}
                    />

                    <Select
                      size="sm"
                      value={step.type}
                      onChange={(_, value) => handleUpdateStep(step.id, { type: value as OnboardingStepType })}
                      sx={{ minWidth: 180 }}
                    >
                      {STEP_TYPES.map((st) => (
                        <Option key={st.type} value={st.type}>
                          {st.label}
                        </Option>
                      ))}
                    </Select>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography level="body-xs">Required</Typography>
                      <Switch
                        size="sm"
                        checked={step.required}
                        onChange={(e) => handleUpdateStep(step.id, { required: e.target.checked })}
                      />
                    </Box>

                    <IconButton
                      size="sm"
                      color="danger"
                      variant="plain"
                      onClick={() => handleRemoveStep(step.id)}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </Box>

                  <Typography level="body-xs" color="neutral">
                    {STEP_TYPES.find((st) => st.type === step.type)?.description}
                  </Typography>
                </Box>
              </Card>
            ))}
          </List>

          {(!formData.steps || formData.steps.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-sm" color="neutral">
                No steps added yet. Click "Add Step" to create your onboarding workflow.
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Messages */}
          <Typography level="title-md" sx={{ mb: 2 }}>Messages</Typography>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Welcome Message</FormLabel>
            <Textarea
              value={formData.welcome_message}
              onChange={(e) => setFormData((prev) => ({ ...prev, welcome_message: e.target.value }))}
              placeholder="Message shown when new hire starts onboarding"
              minRows={2}
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Completion Message</FormLabel>
            <Textarea
              value={formData.completion_message}
              onChange={(e) => setFormData((prev) => ({ ...prev, completion_message: e.target.value }))}
              placeholder="Message shown when onboarding is complete"
              minRows={2}
            />
          </FormControl>
          <PackageReadinessPreview draft={formData} packageId={packageId} />
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            startDecorator={<CheckCircleOutlined />}
          >
            {isEditing ? 'Save Changes' : 'Create Package'}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default OnboardingPackageModal;
